import React from 'react';
import classNames from 'classnames';
import { observer } from 'mobx-react-lite';
import { FormatNumberOptions, useIntl } from 'react-intl';
import Box, { BoxProps } from 'ui-box';
import { defaultColors, Icon, minorScale } from '../../npaper';
import { Spinner } from '../../npaper/Spinner';
import { ICostPrice } from '../../stores';

export const CostPriceText = observer(
  ({
    costPrice,
    vertical = false,
    ...props
  }: { costPrice: ICostPrice; vertical?: boolean } & BoxProps<'span'>) => {
    const intl = useIntl();
    return (
      <Box
        is="span"
        {...props}
        display="inline-flex"
        flexDirection={vertical ? 'column' : 'row'}
        position="relative"
        paddingLeft={vertical ? '16px' : undefined}
      >
        {costPrice ? (
          <>
            {costPrice.precise !== null &&
              intl.formatNumber(costPrice.precise, {
                currency: costPrice.precise_currency!,
                currencyDisplay: 'code',
                style: 'currency',
              })}
            {costPrice.maximum !== null && costPrice.minimum !== null && (
              <>
                <span>
                  {intl.formatNumber(costPrice.minimum, {
                    currency: costPrice.minimum_currency!,
                    currencyDisplay: 'code',
                    style: 'currency',
                  })}
                </span>
                {vertical && (
                  <Box
                    is="span"
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    <Icon icon="arrows-vertical" size={12} />
                  </Box>
                )}
                <span>
                  {!vertical && (
                    <Box is="span" marginX={minorScale(2)}>
                      <Icon icon="arrows-horizontal" size={12} />
                    </Box>
                  )}
                  {intl.formatNumber(costPrice.maximum, {
                    currency: costPrice.maximum_currency!,
                    currencyDisplay: 'code',
                    style: 'currency',
                  })}
                </span>
              </>
            )}
          </>
        ) : (
          <Spinner size={16} />
        )}
      </Box>
    );
  }
);

export const ContributionMarginText = observer(
  ({
    contributionMargin: cm,
    vertical = false,
    showIndication = false,
    percent = false,
    ...props
  }: {
    contributionMargin: ICostPrice;
    vertical?: boolean;
    showIndication?: boolean;
    percent?: boolean;
  } & BoxProps<any>) => {
    const intl = useIntl();
    const belowZero =
      cm &&
      ((cm.minimum && cm.minimum <= 0) || (cm.precise && cm.precise <= 0));

    const defaultStyle: FormatNumberOptions = percent
      ? {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
          style: 'percent',
        }
      : {
          maximumFractionDigits: 0,
          minimumFractionDigits: 0,
          currencyDisplay: 'code',
          style: 'currency',
        };
    return (
      <Box
        is="span"
        {...props}
        display="inline-flex"
        flexDirection={vertical ? 'column' : 'row'}
        position="relative"
        paddingLeft={vertical ? '16px' : undefined}
        className={classNames(showIndication && belowZero && 'danger')}
      >
        {cm ? (
          <>
            {cm.precise !== null &&
              intl.formatNumber(cm.precise, {
                ...defaultStyle,
                currency: cm.precise_currency!,
              })}
            {cm.maximum !== null && cm.minimum !== null && (
              <>
                <span>
                  {intl.formatNumber(cm.minimum, {
                    ...defaultStyle,
                    currency: cm.minimum_currency!,
                  })}
                </span>
                {vertical && (
                  <Box
                    is="span"
                    position="absolute"
                    left="0"
                    top="50%"
                    transform="translateY(-50%)"
                  >
                    <Icon
                      icon="arrows-vertical"
                      size={12}
                      color={
                        showIndication && belowZero
                          ? defaultColors.red[500]
                          : undefined
                      }
                    />
                  </Box>
                )}
                <span>
                  {!vertical && (
                    <Box is="span" marginX={minorScale(2)}>
                      <Icon icon="arrows-horizontal" size={12} />
                    </Box>
                  )}
                  {intl.formatNumber(cm.maximum, {
                    ...defaultStyle,
                    currency: cm.maximum_currency!,
                  })}
                </span>
              </>
            )}
          </>
        ) : (
          <Spinner size={16} />
        )}
      </Box>
    );
  }
);
